import React, { useEffect, useState } from "react";
import { Plus, Loader2, Pencil, Trash2, Star, Music2, Search, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  track_name: "", artist_name: "", link: "", moment: "", style: "",
  language: "", mood: "", tags: [], featured_position: 0, artwork: "", preview_url: "",
};

export default function Library() {
  const [songs, setSongs] = useState([]);
  const [moments, setMoments] = useState([]);
  const [popularity, setPopularity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: lib }, { data: cfg }, { data: pop }] = await Promise.all([
      api.get("/admin/library"),
      api.get("/config/moments"),
      api.get("/admin/popularity"),
    ]);
    setSongs(lib.songs || []);
    setMoments(cfg.moments || []);
    setPopularity(pop.popularity || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (s) => {
    setForm({ ...EMPTY, ...s });
    setEditing(s.id);
    setOpen(true);
  };

  const autofill = async () => {
    const q = `${form.track_name} ${form.artist_name}`.trim();
    if (!q) return;
    const { data } = await api.get("/itunes/search", { params: { q } });
    const hit = (data.results || [])[0];
    if (hit) {
      setForm((f) => ({
        ...f, track_name: hit.track_name, artist_name: hit.artist_name,
        link: hit.link || f.link, artwork: hit.artwork || "", preview_url: hit.preview_url || "",
      }));
      toast.success("Dados preenchidos pelo iTunes.");
    } else {
      toast.error("Nada encontrado.");
    }
  };

  const save = async () => {
    if (!form.track_name || !form.moment) {
      toast.error("Informe m\u00fasica e momento.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, featured_position: Number(form.featured_position) || 0 };
      if (editing) await api.put(`/admin/library/${editing}`, payload);
      else await api.post("/admin/library", payload);
      setOpen(false);
      toast.success("Salvo!");
      load();
    } catch {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/library/${id}`);
    toast.success("Removida.");
    load();
  };

  const removePopular = async (moment, s) => {
    await api.post("/admin/popularity/delete", {
      moment, track_name: s.track_name, artist_name: s.artist_name || "",
    });
    toast.success("Recomendação removida.");
    load();
  };

  const grouped = songs.reduce((acc, s) => {
    (acc[s.moment] = acc[s.moment] || []).push(s);
    return acc;
  }, {});

  const totalPopular = popularity.reduce((n, r) => n + (r.songs || []).length, 0);

  const LibrarySection = () => (
    loading ? (
      <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-marsala)]" /></div>
    ) : songs.length === 0 ? (
      <div className="mt-6 rounded-2xl border border-[color:var(--brand-border)] bg-white p-8 text-center text-sm text-[color:var(--brand-muted)]">
        Nenhuma música cadastrada ainda. Adicione recomendações por momento.
      </div>
    ) : (
      <div className="mt-6 space-y-6">
        {Object.entries(grouped).map(([moment, list]) => (
          <div key={moment}>
            <h3 className="mb-2 font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">{moment}</h3>
            <div className="space-y-2">
              {list.map((s) => (
                <div key={s.id} data-testid={`library-song-${s.id}`} className="flex items-center gap-3 rounded-xl border border-[color:var(--brand-border)] bg-white p-3">
                  {s.artwork ? <img src={s.artwork} alt="" className="h-11 w-11 rounded-lg object-cover" /> : <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[color:var(--brand-paper)]"><Music2 className="h-5 w-5 text-[color:var(--brand-muted)]" /></div>}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[color:var(--brand-graphite)]">{s.track_name}</p>
                    <p className="truncate text-xs text-[color:var(--brand-muted)]">{s.artist_name}{s.style ? ` \u00b7 ${s.style}` : ""}{s.language ? ` \u00b7 ${s.language}` : ""}</p>
                  </div>
                  {s.featured_position > 0 && <Star className="h-4 w-4 fill-[color:var(--brand-champagne)] text-[color:var(--brand-champagne)]" />}
                  <button onClick={() => openEdit(s)} className="rounded-lg p-2 text-[color:var(--brand-muted)] hover:bg-[color:var(--brand-paper)]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(s.id)} className="rounded-lg p-2 text-[color:var(--brand-danger)] hover:bg-[color:var(--brand-paper)]"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  );

  const PopularSection = () => (
    <div className="mt-6">
      <p className="mb-4 flex items-start gap-2 rounded-xl border border-[color:var(--brand-border)] bg-[color:var(--brand-paper)] p-3 text-sm text-[color:var(--brand-muted)]">
        <Users className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-marsala)]" />
        Músicas realmente escolhidas por outros casais em cada momento. Elas aparecem para os noivos como sugestão. Remova qualquer uma que esteja fora do contexto.
      </p>
      {popularity.length === 0 ? (
        <div className="rounded-2xl border border-[color:var(--brand-border)] bg-white p-8 text-center text-sm text-[color:var(--brand-muted)]">
          Ainda não há recomendações de outros clientes.
        </div>
      ) : (
        <div className="space-y-6">
          {popularity.map((row) => (
            <div key={row.moment}>
              <h3 className="mb-2 font-serif text-lg font-semibold text-[color:var(--brand-marsala)]">{row.moment}</h3>
              <div className="space-y-2">
                {(row.songs || []).map((s, i) => (
                  <div key={`${s.track_name}-${s.artist_name}`} data-testid={`popular-song-${row.moment}-${i}`} className="flex items-center gap-3 rounded-xl border border-[color:var(--brand-border)] bg-white p-3">
                    {s.artwork ? <img src={s.artwork} alt="" className="h-11 w-11 rounded-lg object-cover" /> : <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[color:var(--brand-paper)]"><Music2 className="h-5 w-5 text-[color:var(--brand-muted)]" /></div>}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-[color:var(--brand-graphite)]">{s.track_name}</p>
                      <p className="truncate text-xs text-[color:var(--brand-muted)]">{s.artist_name}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-[color:var(--brand-champagne-soft)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--brand-graphite)]">escolhida {s.count}x</span>
                    <button data-testid={`popular-remove-${row.moment}-${i}`} onClick={() => removePopular(row.moment, s)} title="Remover recomendação" className="rounded-lg p-2 text-[color:var(--brand-danger)] hover:bg-[color:var(--brand-paper)]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-[color:var(--brand-graphite)]">Biblioteca Musical LC</h1>
          <p className="mt-1 text-sm text-[color:var(--brand-muted)]">Recomendações que alimentam as sugestões dos casais.</p>
        </div>
        <Button data-testid="library-add-button" onClick={openNew} className="h-11 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]">
          <Plus className="mr-2 h-4 w-4" /> Nova música
        </Button>
      </div>

      <Tabs defaultValue="lc" className="mt-6">
        <TabsList>
          <TabsTrigger value="lc" data-testid="library-tab-lc">Biblioteca LC</TabsTrigger>
          <TabsTrigger value="clientes" data-testid="library-tab-clientes">
            Recomendações de outros clientes{totalPopular > 0 ? ` (${totalPopular})` : ""}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="lc"><LibrarySection /></TabsContent>
        <TabsContent value="clientes"><PopularSection /></TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{editing ? "Editar m\u00fasica" : "Nova m\u00fasica"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1">
                <Label>Música</Label>
                <Input data-testid="library-track-input" value={form.track_name} onChange={(e) => setForm({ ...form, track_name: e.target.value })} className="mt-1 h-10" />
              </div>
              <div className="flex-1">
                <Label>Artista</Label>
                <Input value={form.artist_name} onChange={(e) => setForm({ ...form, artist_name: e.target.value })} className="mt-1 h-10" />
              </div>
            </div>
            <Button type="button" variant="outline" onClick={autofill} className="h-9 w-full"><Search className="mr-2 h-4 w-4" /> Preencher pelo iTunes</Button>
            <div>
              <Label>Momento recomendado</Label>
              <Select value={form.moment} onValueChange={(v) => setForm({ ...form, moment: v })}>
                <SelectTrigger data-testid="library-moment-select" className="mt-1 h-10"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {moments.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Estilo</Label><Input value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Idioma</Label><Input value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Clima</Label><Input value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })} className="mt-1 h-10" /></div>
              <div><Label>Destaque (0-10)</Label><Input type="number" value={form.featured_position} onChange={(e) => setForm({ ...form, featured_position: e.target.value })} className="mt-1 h-10" /></div>
            </div>
            <div><Label>Link</Label><Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-1 h-10" placeholder="YouTube / Spotify / Apple" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="library-save-button" onClick={save} disabled={saving} className="bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
